export type PinLockState = {
  hasPin: boolean;
  timeoutSeconds: number;
  locked: boolean;
  lastActivityAt: number;
  settingsCheckedAt?: number;
};
export const PIN_LOCK_CHANGED = 'pos-pin-lock-changed';
export const getPinLockKey = () => {
  let user: any = {};
  try { user = JSON.parse(localStorage.getItem('posUserData') || '{}'); } catch { /* use username fallback */ }
  return `posPinLock:${String(user?.id || user?.username || localStorage.getItem('posUser') || 'unknown').trim().toLowerCase()}`;
};
const defaults = (): PinLockState => ({ hasPin: false, timeoutSeconds: 180, locked: false, lastActivityAt: Date.now() });
export const readPinLock = (key: string): PinLockState => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return defaults();
    const state = JSON.parse(raw);
    if (typeof state.hasPin !== 'boolean' || typeof state.locked !== 'boolean'
      || !Number.isFinite(state.lastActivityAt) || !Number.isFinite(state.timeoutSeconds)) throw new Error('Invalid lock state');
    const timeoutSeconds = Math.max(30, state.timeoutSeconds);
    return { ...state, timeoutSeconds, locked: state.locked || (state.hasPin && Date.now() - state.lastActivityAt >= timeoutSeconds * 1000) };
  } catch {
    return { ...defaults(), hasPin: true, locked: true }; // A corrupt record must not unlock the screen.
  }
};
export const writePinLock = (key: string, state: PinLockState) => {
  localStorage.setItem(key, JSON.stringify(state));
  window.dispatchEvent(new CustomEvent(PIN_LOCK_CHANGED, { detail: key }));
};
export const canUsePinLockCache = (key: string) => {
  const state = readPinLock(key);
  // An already locked screen can show PIN entry immediately; verification still goes to the server.
  if (state.hasPin && state.locked) return true;
  const age = Date.now() - Number(state.settingsCheckedAt);
  return Number.isFinite(age) && age >= 0 && age < 5 * 60 * 1000;
};
export const configurePinLock = (key: string, settings: { hasPin: boolean; timeoutSeconds: number }) => {
  const current = readPinLock(key);
  const next = { ...current, hasPin: settings.hasPin, timeoutSeconds: Math.max(30, Number(settings.timeoutSeconds) || 180), settingsCheckedAt: Date.now() };
  // A successful settings fetch is not authentication and must not clear an existing lock.
  next.locked = current.locked || (next.hasPin && Date.now() - next.lastActivityAt >= next.timeoutSeconds * 1000);
  writePinLock(key, next);
  return next;
};
export const unlockPinAfterVerification = (key = getPinLockKey()) => {
  writePinLock(key, { ...readPinLock(key), locked: false, lastActivityAt: Date.now() });
};
export const touchPinLock = (key: string) => {
  const state = readPinLock(key);
  if (state.locked) return state;
  const next = { ...state, lastActivityAt: Date.now() };
  writePinLock(key, next);
  return next;
};
